/**
 * GTO Δ computation: for each hero action in a replayed hand, compute the
 * ORACLE-persona GTO recommendation from the pre-action state and classify
 * the hero's real action as good / okay / mistake.
 *
 * Deterministic given a fixed seed (rng is seeded off the hand seed + index).
 * No DB persistence — callers memoize per hand.
 */
import { createPokersolverEvaluator } from '@/engine/handEvaluator';
import type { HandEvaluator } from '@/engine/ports';
import { createRng } from '@/engine/rng';
import { legalActions } from '@/engine/rules';
import type { Action, HandState, PlayerId } from '@/engine/types';
import { PERSONALITIES } from '@/gto/personality';
import { createGtoBot } from '@/gto/solver';
import type { Frame } from './buildFrames';

export type Verdict = 'good' | 'okay' | 'mistake';

export type GtoDeltaEntry = {
  frameIndex: number;
  heroAction: Action;
  recommended: Action | null;
  verdict: Verdict;
};

function actionSize(a: Action): number {
  if (a.type === 'raise') return a.to;
  if ('amount' in a) return a.amount;
  return 0;
}

export function classifyVerdict(hero: Action, gto: Action): Verdict {
  if (hero.type === gto.type) {
    if (hero.type === 'fold' || hero.type === 'check') return 'good';
    const diff = Math.abs(actionSize(hero) - actionSize(gto));
    if (diff === 0) return 'good';
    if (diff < 80) return 'okay';
    return 'mistake';
  }
  // Type mismatch: folding when GTO says otherwise (or vice versa) is a
  // mistake; everything else is only "okay" (different path, similar EV).
  if (hero.type === 'fold' || gto.type === 'fold') return 'mistake';
  return 'okay';
}

function computeRecommendation(
  state: HandState,
  heroId: PlayerId,
  seed: string,
  index: number,
  evaluator: HandEvaluator,
): Action | null {
  const legals = legalActions(state, heroId);
  if (legals.length === 0) return null;
  const rng = createRng(`gto-delta:${seed}:${index}`);
  try {
    const bot = createGtoBot(PERSONALITIES.ORACLE, { rng, evaluator, trials: 400 });
    const decision = bot.decide(state, heroId, legals);
    return decision instanceof Promise ? null : decision;
  } catch {
    return null;
  }
}

export type ComputeOptions = {
  evaluator?: HandEvaluator;
};

export function computeGtoDelta(
  frames: ReadonlyArray<Frame>,
  heroId: PlayerId,
  options: ComputeOptions = {},
): GtoDeltaEntry[] {
  const evaluator = options.evaluator ?? createPokersolverEvaluator();
  const entries: GtoDeltaEntry[] = [];
  for (let i = 1; i < frames.length; i++) {
    const frame = frames[i];
    if (!frame) continue;
    const heroAction = frame.lastAction;
    if (!heroAction || heroAction.playerId !== heroId) continue;
    const preFrame = frames[i - 1];
    if (!preFrame) continue;
    const seed = preFrame.state.seed;
    const recommended = computeRecommendation(preFrame.state, heroId, seed, i, evaluator);
    const verdict: Verdict = recommended ? classifyVerdict(heroAction, recommended) : 'okay';
    entries.push({
      frameIndex: i,
      heroAction,
      recommended,
      verdict,
    });
  }
  return entries;
}
